// The screensaver's two ISS shows:
//
//   ?show=iss     the station from outside, in the Track the ISS framing
//                 (behind and above along its orbit, the horizon level) with
//                 a slow drift round it
//   ?show=cupola  inside the Cupola looking out towards the horizon, the head
//                 resting on the side windows in turn, as the Cupola stream
//                 does (src/ui/cupolaauto.js)
//
// Both run at true scale on the real clock, through the viewer's own ISS
// tracking and Cupola (src/ui/isstrack.js, src/ui/cupola.js), which main.js
// builds in the screensaver only for these two shows. Their card and bar are
// hidden by the screensaver's CSS. The station is tracked once its elements
// have arrived, or after ELEMENTS_WAIT_S on the authored orbit if they never
// do, so the fly-in does not land and then jump to the real position.
import * as THREE from 'three';
import { stepIntoCupola, cupolaHeadLook } from '../ui/cupolaauto.js';

const DEG = Math.PI / 180;
const ELEMENTS_WAIT_S = 12;
// No station after this long (the model failed to load): tick() returns
// false and main.js plays the Earth's show instead.
const GIVE_UP_S = 90;
const STEP_S = 0.5;
// The drift round the station from outside: a swing about its local vertical,
// a rise and fall, and a slow breath in and out. The periods share no small
// multiple, so the frame does not come round again for hours. It eases in
// over EASE_IN_S from the tracking framing.
const YAW_SWING = 38 * DEG, YAW_PERIOD = 420;
const RISE_SWING = 6 * DEG, RISE_PERIOD = 290;
const BREATH = 0.12, BREATH_PERIOD = 230;
const EASE_IN_S = 40;

const smoothstep = (x) => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));

// ctx: { show: 'iss' | 'cupola', drift (the tour's ?drift=, a pace for the
//        drift and the head; 1 by default), camera, controls, getIss(),
//        getEarth(), getSatrec() }
export function createIssScreensaverShow(ctx) {
  const { camera, controls } = ctx;
  const pace = ctx.drift ?? 1;
  let started = -1;
  let lastStep = -Infinity;
  let arrived = -1;
  let gaveUp = false;
  const last = { yaw: 0, rise: 0, scale: 1 };
  const centre = new THREE.Vector3(), earth = new THREE.Vector3(), up = new THREE.Vector3();
  const off = new THREE.Vector3(), aim = new THREE.Vector3(), right = new THREE.Vector3();

  // screensaver/record-assets.mjs waits for a scene name, as on the tour.
  function scene(name) {
    if (document.body.dataset.ssScene !== name) document.body.dataset.ssScene = name;
  }

  function tracked() {
    const track = window.__issTrack;
    return Boolean(track && track.selected && !track.animating);
  }

  // Rotates the camera and the look point about the station by the change
  // in the drift since the last frame. Applied as a change, so the viewer's
  // ISS follow (which carries the framing round the orbit) composes with it.
  function drift(since) {
    const iss = ctx.getIss(), body = ctx.getEarth();
    if (!iss || !body) return;
    const e = smoothstep(since / EASE_IN_S);
    const yaw = e * YAW_SWING * Math.sin((2 * Math.PI * since) / YAW_PERIOD);
    const rise = e * RISE_SWING * Math.sin((2 * Math.PI * since) / RISE_PERIOD);
    const scale = 1 + e * BREATH * Math.sin((2 * Math.PI * since) / BREATH_PERIOD);
    iss.getWorldPosition(centre);
    up.copy(centre).sub(body.getWorldPosition(earth)).normalize();
    off.copy(camera.position).sub(centre);
    aim.copy(controls.target).sub(centre);
    off.applyAxisAngle(up, yaw - last.yaw);
    aim.applyAxisAngle(up, yaw - last.yaw);
    right.crossVectors(off, up);
    if (right.lengthSq() > 1e-30) {
      right.normalize();
      off.applyAxisAngle(right, rise - last.rise);
      aim.applyAxisAngle(right, rise - last.rise);
    }
    off.multiplyScalar(scale / last.scale);
    camera.position.copy(centre).add(off);
    controls.target.copy(centre).add(aim);
    camera.lookAt(controls.target);
    last.yaw = yaw; last.rise = rise; last.scale = scale;
  }

  function step(since) {
    const track = window.__issTrack;
    if (!track || !track.found) {
      if (since > GIVE_UP_S) gaveUp = true;
      return;
    }
    if (!ctx.getSatrec() && since < ELEMENTS_WAIT_S) return;
    if (ctx.show === 'cupola') { stepIntoCupola(); return; }
    if (!track.selected && !track.animating) track.track();
  }

  function tick(elapsed) {
    if (gaveUp) return false;
    if (started < 0) started = elapsed;
    const since = elapsed - started;
    if (ctx.show === 'cupola') {
      const cupola = window.__cupola;
      if (cupola && cupola.phase === 'inside') {
        scene('cupola');
        const { yaw, pitch } = cupolaHeadLook(since * pace);
        cupola.look(yaw, pitch);
      } else if (elapsed - lastStep > STEP_S) {
        lastStep = elapsed;
        step(since);
      }
      return true;
    }
    if (tracked()) {
      if (arrived < 0) { arrived = elapsed; last.yaw = 0; last.rise = 0; last.scale = 1; scene('iss'); }
      drift((elapsed - arrived) * pace);
    } else {
      // Lost the station (a refocus): track it again and start the drift over.
      arrived = -1;
      if (elapsed - lastStep > STEP_S) { lastStep = elapsed; step(since); }
    }
    return true;
  }

  return { tick };
}
